
import { mapState } from 'vuex'
import Cert from './cert.vue'
import CtPhoneVerify from '@/components/phoneVerify/index.vue'
import { fetchWebFlowConfig, matchAllWebFlowRules } from '@/utils/receiptProofRule'

export default {
  components: {
    Cert,
    CtPhoneVerify,
  },
  model: {
    prop: 'modalValue',
    event: 'updateModalValue',
  },
  props: {
    waybillNo: {
      type: String,
      default: '',
    },
    modalValue: {
      type: Boolean,
      default: false,
    },
    destCountryCode: {
      type: String,
      default: '',
    },
    sourceCountryCode: {
      type: String,
      default: '',
    },
    skipVerify: {
      type: Boolean,
      default: false,
    },
    isLoggedIn: {
      type: Boolean,
      default: false,
    },
    isPseudoLogin: {
      type: Boolean,
      default: false,
    },
    encryptedPhone: {
      type: String,
      default: '',
    },
    cargoTypeCode: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      businessCode: '4',
      certModal: false,
      certImagesData: {},
      timer: null,
      isRpod: false,
      isPostAssembly: false,
      isCheckingOwnership: false,
      ownershipCheckDone: false,
      showReceiptProof: false,
      showDeliveryCert: false,
      showSignReceipt: false,
      showSignedDO: false,
      showPostAssembly: false,
      // 中港澳台互寄脱敏时通过 /bills/id/routes 反查得到的路由数据（含始发/目的国家代码）
      fetchedRoute: null,
    }
  },
  computed: {
    ...mapState({
      isMobile: (state) => state.isMobile,
    }),
    visible: {
      get() {
        if (this.skipVerify) {
          return false
        }
        if (this.modalValue && (this.isLoggedIn || this.isPseudoLogin) && this.isCheckingOwnership) {
          return false
        }
        if (this.modalValue && (this.isLoggedIn || this.isPseudoLogin) && this.ownershipCheckDone) {
          return true
        }
        return this.modalValue
      },
      set(v) {
        this.$emit('updateModalValue', v)
      },
    },
    // 签单返还需要接口返回内容+配置都是true
    signedDo() {
      return this.showSignedDO && this.isRpod
    },
    // 宅配延伸需要接口返回内容+配置都是true
    postAssembly() {
      return this.showPostAssembly && this.isPostAssembly
    },
    // 城市隐藏脱敏时 props 国家代码为空，使用路由反查结果兜底
    resolvedDestCountryCode() {
      return this.destCountryCode || this.fetchedRoute?.destCountryCode || ''
    },
    resolvedSourceCountryCode() {
      return this.sourceCountryCode || this.fetchedRoute?.sourceCountryCode || ''
    },
    
  },
  watch: {
    modalValue(val) {
      if (val && (this.isLoggedIn || this.isPseudoLogin) && !this.skipVerify && !this.ownershipCheckDone) {
        this.checkOwnership()
      }
      if (!val) {
        this.ownershipCheckDone = false
        this.isCheckingOwnership = false
      }
    },
    sourceCountryCode() {
      this.checkReceiptProofEligibility()
    },
    destCountryCode() {
      this.checkReceiptProofEligibility()
    },
  },
  mounted() {
    this.checkReceiptProofEligibility()
  },
  beforeDestroy() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  },
  methods: {
    // 统一 CMS 配置检查：获取 web_proof_flow_rules 并匹配 5 个区块
    async checkReceiptProofEligibility() {
      try {
        const config = await fetchWebFlowConfig(this.$API.support.getSysConfig)
        const result = matchAllWebFlowRules(
          config,
          this.cargoTypeCode,
          this.resolvedSourceCountryCode,
          this.resolvedDestCountryCode
        )
        this.showReceiptProof = result.showReceiptProof
        this.showDeliveryCert = result.showDeliveryCert
        this.showSignReceipt = result.showSignReceipt
        this.showSignedDO = result.showSignedDO
        this.showPostAssembly = result.showPostAssembly
        this.businessCode = result.businessCode
      } catch (e) {
        this.showReceiptProof = false
        this.showDeliveryCert = false
        this.showSignReceipt = false
        this.showSignedDO = false
        this.showPostAssembly = false
        this.businessCode = '4'
      }
    },
    validateInfo(params, cap) {
      this.ensureRouteDataThenQuery(params, cap)
    },
    handleAutoVerify() {
      this.ensureRouteDataThenQuery({ waybillNo: this.waybillNo, mobileNoLast4: '' }, null)
    },
    async checkOwnership() {
      if (this.isCheckingOwnership) return
      this.isCheckingOwnership = true

      try {
        if (this.isLoggedIn) {
          const res = await this.$API.support.getWaybillRel({ waybillNo: this.waybillNo })
          if (res?.result === true) {
            this.ownershipCheckDone = true
            this.$emit('updateModalValue', false)
            this.ensureRouteDataThenQuery({ waybillNo: this.waybillNo }, null)
            return
          }
        } else if (this.isPseudoLogin) {
          const res = await this.$API.support.checkPhoneWaybillRel({
            encryptedPhone: decodeURIComponent(this.encryptedPhone),
            waybillNo: this.waybillNo,
          })
          if (res?.result === true) {
            this.ownershipCheckDone = true
            this.$emit('updateModalValue', false)
            this.ensureRouteDataThenQuery({ waybillNo: this.waybillNo, encryptedPhone: decodeURIComponent(this.encryptedPhone) }, null)
            return
          }
        }
        this.isCheckingOwnership = false
        this.ownershipCheckDone = true
        this.$emit('updateModalValue', true)
      } catch (error) {
        console.error('Ownership check failed:', error)
        this.isCheckingOwnership = false
        this.ownershipCheckDone = true
        this.$emit('updateModalValue', true)
      }
    },
    // 中港澳台互寄 cityHidden=true 时 props 国家代码被脱敏为空，
    // 先调用 /bills/id/routes 反查路由，等数据返回后再调用 imagesQuery；
    // 路由返回的始发/目的国家代码同时用于头部 tab 标签的规则匹配
    ensureRouteDataThenQuery(params, cap) {
      if (!this.waybillNo) return
      if (this.destCountryCode || this.fetchedRoute) {
        this.getImagesQuery(params, cap)
        return
      }
      this.$API.support
        .billRouteApi(
          {
            waybillNo: this.waybillNo,
            businessCode: this.businessCode,
            ...(params.mobileNoLast4 && { mobileNoLast4: params.mobileNoLast4 }),
            ...(params.encryptedPhone && { encryptedPhone: params.encryptedPhone }),
          },
          cap
        )
        .then((routeRes) => {
          if (routeRes && routeRes.length) {
            this.fetchedRoute = routeRes[0]
            // tab 规则依赖国家代码，拿到路由数据后重新匹配
            this.checkReceiptProofEligibility()
          }
          // 等待路由数据返回之后再次调用 imagesQuery
          this.getImagesQuery(params, cap)
        })
        .catch((e) => {
          // 反查失败不阻断签收证明主流程，destCountryCode 留空（后端选填）
          console.error('Fetch route data failed:', e)
          this.getImagesQuery(params, cap)
        })
    },
    // 获取图片（请求所有 tab 需要的 imageType）
    getImagesQuery(params, cap) {
      if (this.waybillNo) {
        const opts = {
          app: 'receipt',
          imageType: '2,5,15,71,68,252',
          waybillNo: params.waybillNo,
          businessCode: this.businessCode,
          ...(params.mobileNoLast4 && { mobileNoLast4: params.mobileNoLast4 }),
          ...(params.encryptedPhone && { encryptedPhone: params.encryptedPhone }),
          destCountryCode: this.resolvedDestCountryCode,
        }
        const apiCall = cap
          ? this.$API.support.getImagesQuery(opts, cap)
          : this.$API.support.getImagesQuery(opts)
        apiCall.then((res) => {
          if (res) {
            this.visible = false
            this.isRpod = res.rpod
            this.isPostAssembly = res.postAssembly
            if (res.waybillImageVOList) {
              const data = res.waybillImageVOList
              if (this.isMobile) {
                this.mobileView(data)
              } else {
                this.pcView(data)
              }
            } else {
              this.$message.info(this.$t('common.tips.noData'))
            }
          }
        })
      }
    },
    pcView(res) {
      if (this.timer) {
        clearTimeout(this.timer)
      }
      this.timer = setTimeout(() => {
        this.certModal = true
        this.certImagesData = res
      }, 500)
    },
    mobileView(imageArr) {
      this.$router.push({
        name: 'site-lang-support-querySupport-waybill-mobile-signDelivery',
        params: {
          site: this.$route.params.site,
          lang: this.$route.params.lang,
          imageArr,
          showReceiptProof: this.showReceiptProof,
          showDeliveryCert: this.showDeliveryCert,
          showSignReceipt: this.showSignReceipt,
          showSignedDO: this.signedDo,
          showPostAssembly: this.postAssembly,
          destCountryCode: this.resolvedDestCountryCode,
        },
      })
    },
  },
}
